/**
 *  @flow
 */

import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon } from 'semantic-ui-react';
import { omit } from 'lodash';
import uuid from 'uuid';
import { format } from 'date-fns';

import { Modal, Button } from '../../../components';
import { SeminarForm } from './form';
import { ADD_SEMINAR_QUERY, GET_SEMINARS_QUERY } from '../containers/queries';

const intlMessages = defineMessages({
    nameExistingsError: {
        id: 'pages.seminars.AddSeminarDialog.nameExistingsError',
        defaultMessage: 'Seminar with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

function AddSeminarDialog({ trigger, open, onClose, intl }) {
    const supplier = useSelector(({ user }) => user.get('id'));
    const searchQuery = useSelector(({ seminars }) => seminars.get('searchQuery'));

    const methods = useForm({
        defaultValues: {
            name: '',
            placeCount: '',
            description: '',
        },
    });
    const { handleSubmit, reset, setError, formState: { isSubmitting, isDirty } } = methods;

    const [addSeminar] = useMutation(ADD_SEMINAR_QUERY, {
        update: (store, { data: { addSeminar: result } }) => {
            try {
                const variables = { search: searchQuery || undefined, first: 20 };
                const data = store.readQuery({ query: GET_SEMINARS_QUERY, variables });
                const newSeminar = {
                    node: result.seminar,
                    __typename: 'ClientEdge',
                    cursor: btoa(result.seminar.id),
                };
                store.writeQuery({
                    query: GET_SEMINARS_QUERY,
                    variables,
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            seminars: {
                                ...data.viewer.seminars,
                                edges: [newSeminar, ...data.viewer.seminars.edges],
                            },
                        },
                    },
                });
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onSubmit = async formData => {
        const startAt = `${format(new Date(formData.startAt), 'yyyy-MM-dd')} ${formData.eventTime}`;
        const placeCount = parseInt(formData.placeCount);
        const banner = formData.banner && formData.banner[0];

        try {
            await addSeminar({
                variables: {
                    input: {
                        ...omit(formData, ['banner', 'startAt', 'eventTime', 'placeCount']),
                        supplier,
                        startAt,
                        banner,
                        placeCount,
                    },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    addSeminar: {
                        __typename: 'AddSeminarPayload',
                        seminar: {
                            __typename: 'Seminar',
                            id: uuid.v4(),
                            ...omit(formData, ['banner', 'startAt', 'eventTime', 'description']),
                            supplier,
                            startAt,
                            description: formData.description || '',
                            banner: { __typename: 'SeminarBanner', url: '' },
                            visitors: { count: 0, list: [] },
                        },
                    },
                },
            });
            reset();
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'AlreadyExists' && graphQLError.data?.error === 'NAME_ALREADY_EXISTS') {
                    setError('name', { message: intl.formatMessage(intlMessages.nameExistingsError) });
                    return;
                }
                setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.seminars.AddSeminarDialog.header"
                        defaultMessage="New Seminar"
                    />
                </Modal.Header>
                <Modal.Content>
                    <SeminarForm createMode />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.seminars.AddSeminarDialog.add"
                            defaultMessage="Add"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.seminars.AddSeminarDialog.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(AddSeminarDialog);
